import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

interface UserSettingsContextType {
  showUnvocalizedText: boolean;
  setShowUnvocalizedText: (value: boolean) => void;
  isLoading: boolean;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export function UserSettingsProvider({ children }: { children: React.ReactNode }) {
  const [showUnvocalizedText, setShowUnvocalizedTextState] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Listen to auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load settings from database when user logs in
  useEffect(() => {
    const loadSettings = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('show_unvocalized_text')
          .eq('user_id', userId)
          .single();

        if (error) {
          console.error('Error loading user settings:', error);
        } else if (data) {
          setShowUnvocalizedTextState(data.show_unvocalized_text ?? true);
        }
      } catch (err) {
        console.error('Error loading user settings:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [userId]);

  const setShowUnvocalizedText = useCallback(async (value: boolean) => {
    setShowUnvocalizedTextState(value);

    if (!userId) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ show_unvocalized_text: value })
        .eq('user_id', userId);

      if (error) {
        console.error('Error saving user settings:', error);
      }
    } catch (err) {
      console.error('Error saving user settings:', err);
    }
  }, [userId]);

  return (
    <UserSettingsContext.Provider value={{ showUnvocalizedText, setShowUnvocalizedText, isLoading }}>
      {children}
    </UserSettingsContext.Provider>
  );
}

export function useUserSettings() {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    // Return default values if used outside provider
    return {
      showUnvocalizedText: true,
      setShowUnvocalizedText: () => {},
      isLoading: false,
    };
  }
  return context;
}
